import { useState } from "react";
import type { ReactNode } from "react";
import brandLogo from "../../../../assets/images/tnp-logo-neon.png";
import AppLayout from "../../../../shared/components/AppLayout";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import "./LegalPages.css";

const PRIVACY_SECTION_ICONS = [
  "database",
  "manage_accounts",
  "lock",
  "share",
  "link",
  "person_check",
  "verified_user",
  "info",
] as const;

export default function PrivacyPolicyPage() {
  const text = siteTexts.legal.privacyPolicy;
  const [linkPrefix, linkSuffix = ""] =
    text.paragraphs[4].split("__PRIVACY_LINK__");
  const sections: Array<{
    title: string;
    content: ReactNode;
  }> = [
    {
      title: text.collectionTitle,
      content: <p>{text.paragraphs[0]}</p>,
    },
    {
      title: text.usageTitle,
      content: <p>{text.paragraphs[1]}</p>,
    },
    {
      title: text.storageSecurityTitle,
      content: <p>{text.paragraphs[2]}</p>,
    },
    {
      title: text.dataSharingTitle,
      content: <p>{text.paragraphs[3]}</p>,
    },
    {
      title: text.externalLinksTitle,
      content: (
        <p>
          {linkPrefix}
          <a
            href="https://politicaprivacidade.com/"
            rel="noopener noreferrer"
            target="_blank"
          >
            {text.privacyLinkLabel}
          </a>
          {linkSuffix}
        </p>
      ),
    },
    {
      title: text.userRightsTitle,
      content: (
        <>
          <p>{text.paragraphs[5]}</p>
          <p>{text.paragraphs[6]}</p>
        </>
      ),
    },
    {
      title: text.commitmentTitle,
      content: (
        <>
          <p>{text.commitmentIntro}</p>
          <ul>
            {text.commitmentItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </>
      ),
    },
    {
      title: text.moreInfoTitle,
      content: (
        <>
          <p>{text.moreInfoParagraph}</p>
          <p>{text.effectiveDate}</p>
        </>
      ),
    },
  ];
  const [openSections, setOpenSections] = useState<string[]>(() =>
    sections.map((section) => section.title),
  );

  const toggleSection = (title: string) => {
    setOpenSections((current) =>
      current.includes(title)
        ? current.filter((item) => item !== title)
        : current.concat(title),
    );
  };

  return (
    <AppLayout>
      <main className="legal-terms-page" aria-labelledby="privacy-page-title">
        <div className="legal-terms-shell">
          <header className="legal-terms-hero">
            <img className="legal-terms-logo" src={brandLogo} alt="TNP" />
            <div className="legal-terms-hero-content">
              <span
                className="legal-terms-hero-icon material-symbols-outlined"
                aria-hidden="true"
              >
                shield_lock
              </span>
              <div>
                <h1 id="privacy-page-title">{text.title}</h1>
                <p>
                  {text.lastUpdatedLabel}: {text.lastUpdatedDate}
                </p>
              </div>
            </div>
          </header>

          <section className="legal-terms-intro">
            <span
              className="legal-terms-intro-icon material-symbols-outlined"
              aria-hidden="true"
            >
              lock
            </span>
            <div>
              <h2>{text.introTitle}</h2>
              <p>{text.introDescription}</p>
            </div>
          </section>

          <section className="legal-terms-list" aria-label={text.title}>
            {sections.map((section, index) => {
              const isOpen = openSections.includes(section.title);
              const panelId = `privacy-section-${index}`;
              const buttonId = `privacy-section-trigger-${index}`;
              return (
                <article className="legal-terms-section" key={section.title}>
                  <span
                    className="legal-terms-section-icon material-symbols-outlined"
                    aria-hidden="true"
                  >
                    {PRIVACY_SECTION_ICONS[index] || "description"}
                  </span>
                  <div className="legal-terms-section-main">
                    <h2>{section.title}</h2>
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={buttonId}
                      hidden={!isOpen}
                    >
                      {section.content}
                    </div>
                  </div>
                  <button
                    id={buttonId}
                    type="button"
                    className="legal-terms-section-toggle"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    aria-label={`${isOpen ? text.collapseLabel : text.expandLabel}: ${section.title}`}
                    onClick={() => toggleSection(section.title)}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      expand_more
                    </span>
                  </button>
                </article>
              );
            })}
          </section>

          <section className="legal-terms-contact">
            <span
              className="legal-terms-contact-icon material-symbols-outlined"
              aria-hidden="true"
            >
              contact_support
            </span>
            <div>
              <h2>{text.questionsTitle}</h2>
              <p>{text.questionsText}</p>
            </div>
            <a className="legal-terms-contact-button" href="mailto:todosnopadel@gmail.com">
              {text.contactCta}
              <span className="material-symbols-outlined" aria-hidden="true">
                arrow_forward
              </span>
            </a>
          </section>
        </div>
      </main>
    </AppLayout>
  );
}
